import type { Editor } from "@tiptap/core";
import { useContentContext } from "components/Documents/Content/content-context";
import { usePrompt } from "hooks";
import { useReportsActions } from "store/actions-context";

import type { InlineMeasureClickData } from "./InlineMeasureExtension";
import { MeasurePrompt } from "./MeasurePrompt";

export const inlineMeasureAttributes: Record<string, string> = {
  measureId: "data-measure-id",
  measureDataSettings: "data-measure-data-settings"
};

export type UseInlineMeasureTiptap = () => {
  handleInlineMeasureClick: (data: InlineMeasureClickData) => Promise<void>;
  insertInlineMeasureElement: (editor: Editor) => Promise<void>;
  updateMeasures: (editor: Editor) => Promise<void>;
};

export const useInlineMeasureTiptap: UseInlineMeasureTiptap = () => {
  const openPrompt = usePrompt();
  const { getInlineMeasureDataAction } = useReportsActions();
  const { report_queries } = useContentContext();

  const updateMeasures = async (editor: Editor) => {
    const measures: { measureId: string; measureDataSettings: string; nodePos: number }[] = [];

    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "inlineMeasure" && node.attrs.measureId && node.attrs.measureDataSettings) {
        measures.push({
          measureId: node.attrs.measureId,
          measureDataSettings: node.attrs.measureDataSettings,
          nodePos: pos
        });
      }
    });

    if (measures.length === 0) return;

    // Add skeleton class to all measure DOM elements
    measures.forEach(({ nodePos }) => {
      const dom = editor.view.nodeDOM(nodePos);
      if (dom && dom instanceof HTMLElement) {
        dom.classList.add("inline-measure-skeleton");
      }
    });

    // Fetch fresh data for each measure
    const results = await Promise.allSettled(
      measures.map(async ({ measureId, measureDataSettings }) => {
        try {
          const settings = JSON.parse(measureDataSettings);
          const measureData = await getInlineMeasureDataAction(settings);
          return { measureId, measureData };
        } catch {
          return null;
        }
      })
    );

    // Update each measure: remove skeleton and set new content (or remove skeleton on failure)
    const tr = editor.state.tr;
    results.forEach((result) => {
      if (result.status !== "fulfilled" || result.value === null) return;

      const { measureId, measureData } = result.value;

      let nodePos: number | null = null;
      tr.doc.descendants((node, pos) => {
        if (node.type.name === "inlineMeasure" && node.attrs.measureId === measureId) {
          nodePos = pos;
          return false;
        }
      });

      if (nodePos === null) return;

      const node = tr.doc.nodeAt(nodePos);
      if (!node) return;

      tr.setNodeMarkup(nodePos, undefined, {
        ...node.attrs,
        content: measureData,
        measureDataSettings: node.attrs.measureDataSettings
      });
    });
    editor.view.dispatch(tr);

    // Remove skeleton class from all measures (success: DOM may be reused; failure: was never updated)
    measures.forEach(({ measureId }) => {
      let pos: number | null = null;
      editor.state.doc.descendants((node, p) => {
        if (node.type.name === "inlineMeasure" && node.attrs.measureId === measureId) {
          pos = p;
          return false;
        }
      });
      if (pos === null) return;
      const dom = editor.view.nodeDOM(pos);
      if (dom && dom instanceof HTMLElement) {
        dom.classList.remove("inline-measure-skeleton");
      }
    });
  };

  const handleInlineMeasureClick = async ({
    measureId,
    measureDataSettings,
    element,
    editor
  }: InlineMeasureClickData) => {
    const initialState = JSON.parse(measureDataSettings);

    const result = await openPrompt({
      Component: MeasurePrompt,
      componentType: "popover",
      anchorEl: element,
      props: {
        initialState,
        ...(report_queries && { documentQueries: report_queries })
      }
    });

    if (!result) return;

    const measureData = await getInlineMeasureDataAction(result);
    const measureDataSettingsUpdated = JSON.stringify(result);

    let nodePos: number | null = null;
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "inlineMeasure" && node.attrs.measureId === measureId) {
        nodePos = pos;
        return false;
      }
    });

    if (nodePos === null) return;

    const tr = editor.state.tr;
    const node = tr.doc.nodeAt(nodePos);
    if (!node) return;

    tr.setNodeMarkup(nodePos, undefined, {
      ...node.attrs,
      content: measureData,
      measureDataSettings: measureDataSettingsUpdated
    });
    editor.view.dispatch(tr);
  };

  const insertInlineMeasureElement = async (editor: Editor) => {
    if (!editor) return;

    const { from } = editor.state.selection;
    const rect = editor.view.coordsAtPos(from);

    const anchorElement = document.createElement("div");
    anchorElement.style.position = "fixed";
    anchorElement.style.left = `${rect.left}px`;
    anchorElement.style.top = `${rect.bottom + 5}px`;
    anchorElement.style.width = "1px";
    anchorElement.style.height = "1px";
    anchorElement.style.pointerEvents = "none";
    document.body.appendChild(anchorElement);

    const result = await openPrompt({
      Component: MeasurePrompt,
      componentType: "popover",
      anchorEl: anchorElement,
      props: {
        ...(report_queries && { documentQueries: report_queries })
      }
    });

    document.body.removeChild(anchorElement);

    if (!result) return;

    if (result.selectedOptions && result.selectedOptions.length > 0) {
      const measureData = await getInlineMeasureDataAction(result);

      const measureId = `measure-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const measureDataSettings = JSON.stringify(result);

      editor
        .chain()
        .focus()
        .deleteRange({ from: from - 1, to: from })
        .setInlineMeasure({ measureId, measureDataSettings, content: measureData })
        .run();
    }
  };

  return { handleInlineMeasureClick, insertInlineMeasureElement, updateMeasures };
};
